import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useState } from "react";
import { BarChart3, Bell, Car, Hexagon, History, LayoutDashboard, Menu, Play, Radar, Settings, Siren, Users } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Brand } from "@/components/common";
import { Topbar } from "@/components/layout/Topbar";
import { useFleet } from "@/context/FleetContext";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

export const NAV = [
  ["/", "Dashboard", LayoutDashboard, "dashboard"],
  ["/vehicles", "Kendaraan", Car, "vehicles"],
  ["/live", "Live Tracking", Radar, "live"],
  ["/history", "Riwayat", History, "history"],
  ["/playback", "Playback", Play, "playback"],
  ["/geofence", "Geofence", Hexagon, "geofence"],
  ["/alarms", "Alarm", Siren, "alarms"],
  ["/reports", "Laporan", BarChart3, "reports"],
  ["/users", "Pengguna", Users, "users"],
  ["/settings", "Pengaturan", Settings, "settings"],
];

function NavItems({ onNavigate }) {
  const { isAdmin, user } = useAuth();
  const items = isAdmin
    ? NAV
    : user?.role === "seller"
      ? NAV.map(([to, label, Icon, key]) =>
          to === "/users" ? [to, "Customer", Icon, key] : [to, label, Icon, key]
        )
      : NAV.filter(([to]) => to !== "/users");
  return (
    <nav className="space-y-0.5">
      {items.map(([to, label, Icon, key]) => (
        <NavLink key={to} to={to} end={to === "/"} onClick={onNavigate} data-testid={`nav-${key}`}
          className={({ isActive }) => cn("group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-[13.5px] font-medium transition-colors duration-150",
            isActive ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground")}>
          {({ isActive }) => (
            <>
              {isActive && <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-primary" />}
              <Icon className={cn("h-[18px] w-[18px]", isActive && "text-primary")} />
              {label}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function Sidebar() {
  const { connected } = useFleet();
  const { isDemo } = useAuth();
  return (
    <aside className="fixed inset-y-0 left-0 z-30 hidden w-[248px] flex-col border-r border-border bg-card/60 backdrop-blur-xl lg:flex" data-testid="sidebar">
      <div className="flex h-16 items-center px-5"><Brand /></div>
      <div className="gt-scroll flex-1 overflow-y-auto px-3 py-3">
        <div className="gt-eyebrow mb-2 px-3">Navigasi</div>
        <NavItems />
      </div>
      <div className="m-3 rounded-2xl border border-border bg-background/50 p-3.5">
        <div className="flex items-center gap-2 text-xs font-semibold">
          <span className={cn("h-2 w-2 rounded-full", connected ? "bg-emerald-500 gt-blink" : "bg-red-500")} />
          {isDemo ? "Simulasi aktif" : connected ? "Realtime terhubung" : "Menyambungkan..."}
        </div>
        <div className="mt-1 text-[11px] text-muted-foreground">
          {isDemo ? "Data kendaraan simulasi" : "WebSocket stream posisi GPS"}
        </div>
      </div>
    </aside>
  );
}

function MobileNav() {
  const [open, setOpen] = useState(false);
  const { unread } = useFleet();
  const items = [NAV[0], NAV[2], NAV[1], NAV[6]];
  return (
    <>
      <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/90 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl lg:hidden" data-testid="mobile-nav">
        <div className="grid grid-cols-5">
          {items.map(([to, label, Icon, key]) => (
            <NavLink key={to} to={to} end={to === "/"} data-testid={`mobile-nav-${key}`}
              className={({ isActive }) => cn("relative flex flex-col items-center gap-1 py-2.5 text-[10px] font-semibold transition-colors", isActive ? "text-primary" : "text-muted-foreground")}>
              <Icon className="h-5 w-5" />
              {label.split(" ")[0]}
              {key === "alarms" && unread > 0 && <span className="absolute right-[26%] top-1.5 h-2 w-2 rounded-full bg-red-500" />}
            </NavLink>
          ))}
          <button onClick={() => setOpen(true)} className="flex flex-col items-center gap-1 py-2.5 text-[10px] font-semibold text-muted-foreground" data-testid="mobile-nav-menu">
            <Menu className="h-5 w-5" /> Menu
          </button>
        </div>
      </nav>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl border-border bg-card pb-8" data-testid="mobile-menu-sheet">
          <SheetTitle className="mb-4"><Brand /></SheetTitle>
          <NavItems onNavigate={() => setOpen(false)} />
          <NavLink to="/notifications" onClick={() => setOpen(false)} className="mt-1 flex items-center gap-3 rounded-xl px-3 py-2.5 text-[13.5px] font-medium text-muted-foreground hover:bg-accent" data-testid="mobile-nav-notifications">
            <Bell className="h-[18px] w-[18px]" /> Notifikasi
          </NavLink>
        </SheetContent>
      </Sheet>
    </>
  );
}

export default function AppShell() {
  const loc = useLocation();
  const full = loc.pathname.startsWith("/live");
  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <div className="lg:pl-[248px]">
        <Topbar />
        <main className={cn(full ? "" : "mx-auto max-w-[1600px] px-4 py-6 sm:px-6 lg:px-8 lg:py-8", "pb-24 lg:pb-8")} data-testid="main-content">
          <Outlet />
        </main>
      </div>
      <MobileNav />
    </div>
  );
}
